//@ts-check
import { addRemoveClass, replaceInStyle } from "common/dom-utils";
import {
  shortenSampleTextToLimitHeight,
  useLhUnitsToLimitHeight,
} from "../type-tester/config";
import { applyAscendersDescenderCorrection } from "../type-tester/text-editor/ascender-padding";
import { isSafari } from "common/utils/browser-detection";
import debug from "debug";
import { applyLineHeightGapCorrection } from "../type-tester/text-editor/line-height-gap-correction";
import { logGroup } from "store/javascripts/utils/logging";

const log = debug("type-tester:applySampleFormatting");

/**
 * Applies formatting to the DOM elements of a sample.
 *
 * The purpose of this function is to have a single place where all the formatting is applied,
 * so that we can easily change the algorithm in one place.
 *
 * It will decide on which strategy to use depending on the browser, number of columns, number of lines, etc.
 * Some formatting is not applied here, e.g. (font-family).
 *
 * @param {import("./types").SampleEnvironment} sampleEnvironment
 * @param {import("./types").SampleFormattingResults} results
 */
export function applySampleFormatting(sampleEnvironment, results) {
  logGroup("applySampleFormatting", () => {
    log(`applySampleFormatting`, results, { sampleEnvironment });
    applySampleFormattingInner(sampleEnvironment, results);
  });
}

function applySampleFormattingInner(sampleEnvironment, results) {
  log(`applySampleFormatting`, results, { sampleEnvironment });
  const { renderedTextElement, editorElement, fontSettings, typeTester } =
    sampleEnvironment;

  const columnsElement = editorElement ?? renderedTextElement;

  // The max-height should be on the same element as the columns, otherwise Safari behaves weirdly with overflowing texts
  const maxHeightElement = columnsElement;

  // ========= Font Size =========
  renderedTextElement.style.fontSize = `${results.scaledFontSize}px`;

  // ========= Max Height =========
  /** @type {() => {maxHeightCss: string, useTextShortening: boolean, debugString: string}} */
  const determineMaxHeightCss = () => {
    // --- 1. Force text-shortening if set
    if (sampleEnvironment.settings.maxHeightStrategy === "text") {
      log(`using text-shortening because it's forced by the setting`);
      return {
        maxHeightCss: "",
        useTextShortening: true,
        debugString: `text`,
      };
    }

    // --- 2. One-column & one-line, no-wrap
    const columns = results.columns ?? 1;
    if (
      results.lines &&
      results.lines === 1 &&
      columns === 1 &&
      results.wrap === false
    ) {
      // If it's a single line, let the horizontal overflow be cut off
      return {
        maxHeightCss: "",
        useTextShortening: false,
        debugString: "1line",
      };
    }

    // --- 3. LH max-height
    if (useLhUnitsToLimitHeight() && results.lines && fontSettings) {
      // Use lh units to limit the height - safarimaxheightissue_option8
      // Conditions:
      // - `useLhUnitsToLimitHeight()` - newer Safari versions that support lh units
      // - `results.lines > 1` - because on a single line, we use a non-wrapping single line with no max-height, but overflow-x

      // These values can be determined for each font style on /experiments/lh-max-height
      // safariLhMarginMinLineHeight

      const { safariLhMarginMinLineHeight, safariLhMarginCoefficient } =
        fontSettings;

      let linesWithCorrection = results.lines;

      // Apply a correction if needed/possible
      if (
        safariLhMarginMinLineHeight &&
        safariLhMarginCoefficient &&
        columns > 1
      ) {
        // - `columns > 1` - because single-column is treated differently by Safari and it has a cropping issue
        const lineHeight = results.lineHeight * 100;

        // Difference between the minimum safe line height and the current line height.
        const lineHeightDiff = Math.max(
          0,
          safariLhMarginMinLineHeight - lineHeight
        );

        // Increase the the value used in `max-height: ${x}lh` linearly with the line height difference.
        linesWithCorrection +=
          (safariLhMarginCoefficient / 1000) * lineHeightDiff;
      }

      // If 1 column, line height too low
      if (columns === 1 && isSafari()) {
        const lineHeight = results.lineHeight * 100;
        const { minLineHeightAscender, minLineHeightDescender } =
          fontSettings ?? {};
        log(
          `minLineHeightAscender: ${minLineHeightAscender}, minLineHeightDescender: ${minLineHeightDescender}`
        );
        if (minLineHeightAscender && minLineHeightDescender) {
          const minLineHeight = Math.max(
            minLineHeightAscender,
            minLineHeightDescender
          );
          log(`minLineHeight: ${minLineHeight}, lineHeight: ${lineHeight}`);
          if (lineHeight < minLineHeight) {
            log(`using text-shortening because the line-height is too low`);
            return {
              maxHeightCss: "",
              useTextShortening: true,
              debugString: `text`,
            };
          } else {
            log(
              `not using text-shortening because the line-height is not too low`
            );
          }
        }
      }

      return {
        maxHeightCss: `calc(${linesWithCorrection}lh - var(--ascender-padding) - var(--descender-padding))`,
        useTextShortening: false,
        debugString: `lh`,
      };
    } // if (useLhUnitsToLimitHeight() && results.lines && fontSettings)

    // --- 4. Text-shortening on Safari at low line-heights
    if (results.height === null && shortenSampleTextToLimitHeight()) {
      const { minLineHeightAscender, minLineHeightDescender } =
        fontSettings ?? {};
      if (minLineHeightAscender && minLineHeightDescender) {
        const minLineHeight = Math.max(
          minLineHeightAscender,
          minLineHeightDescender
        );
        if (results.lineHeight < minLineHeight) {
          // If the line height is small
          return {
            maxHeightCss: "",
            useTextShortening: true,
            debugString: "text",
          };
        }
      }
    }

    // --- 5. Pixel max-height
    return {
      maxHeightCss: `${results.height}px`,
      useTextShortening: false,
      debugString: "px",
    };
  };

  const { maxHeightCss, useTextShortening, debugString } =
    determineMaxHeightCss();
  maxHeightElement.style.maxHeight = maxHeightCss;
  // maxHeightElement.style.setProperty("--max-height-debug", debugString);

  if (typeTester) {
    typeTester.textLengthLimiterPlugin?.setEnabled(useTextShortening);
  }

  // ======== Column balance correction =========
  // #balancedcolumns
  const determineColumnBalanceCorrection = () => {
    if (
      (results.columns ?? 1) === 1 ||
      !fontSettings ||
      !(isSafari() && useLhUnitsToLimitHeight())
    ) {
      return "0px";
    }
    const { scaledFontSize: fontSize } = results;
    const lineHeight = results.lineHeight * 100;
    const { safariLhBalanceMinLineHeight } = fontSettings;

    const lhDiff = Math.max(0, safariLhBalanceMinLineHeight - lineHeight);

    const correction = (lhDiff * fontSize) / 200;

    return `${correction}px`;
  };

  columnsElement.style.setProperty(
    "--column-balance-correction",
    determineColumnBalanceCorrection()
  );

  // ======== Wrap =========
  // If it's a single line, let the horizontal overflow be cut off
  renderedTextElement.style.whiteSpace = results.wrap ? "" : "nowrap";
  addRemoveClass(renderedTextElement, "nowrap", !results.wrap);

  // ======== Overflow =========
  const overflowStylesCss =
    results.height !== null || results.lines === 1
      ? "overflow: hidden; overflow: clip;"
      : "";
  replaceInStyle(
    renderedTextElement,
    /overflow\s*:[^;]+;?/gi,
    overflowStylesCss
  );

  // ======== Columns ========
  // Applying column-count 1 makes the browser hide the overflowing line,
  // this way we can allow space for the descenders without including the next line.
  const columns = results.columns ?? 1;

  columnsElement.style.columns = columns.toString();

  // ======== Ascenders and Descenders ========
  if (editorElement) {
    applyAscendersDescenderCorrection(
      editorElement,
      results.ascenderCorrection,
      results.descenderCorrection
    );
  }

  // ======== Line-height gap correction ========
  if (editorElement) {
    applyLineHeightGapCorrection(
      editorElement,
      results.lineHeightGapCorrections
    );
  }

  // ======== Font Size Selector ========
  if (typeTester) {
    if (!typeTester.fontSizeSelector) {
      log(
        `applySampleFormatting: typeTester.fontSizeSelector is not defined, not updating font size selector`
      );
    }
    typeTester.fontSizeSelector?.updateFromScaling({
      scalingFactor: results.scalingFactor,
      scaledFontSize: results.scaledFontSize,
      columnCount: results.columns,
      columnsDeterminedAutomatically: results.columnsDeterminedAutomatically,
    });
  } else {
    log(
      `applySampleFormatting: typeTester is not defined, not updating font size selector`
    );
  }
}
